import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, ListOrdered, FolderTree } from "lucide-react";
import api, { formatErr } from "../../lib/api";

const DEFAULT_NAV = [
  { label: "Shop by Brands", href: "/shop", dropdown: ["Rolex","Patek Philippe","Audemars Piguet","Omega","Cartier","TAG Heuer","Hublot","IWC"] },
  { label: "Watches", href: "/shop", dropdown: ["Luxury","Sport","Diving","Chronograph","Dress","Limited Edition"] },
  { label: "Handbags", href: "/shop?category=handbags", dropdown: ["Hermès","Chanel","Louis Vuitton","Gucci","Prada"] },
  { label: "Leather Goods", href: "/p/leather-goods" },
  { label: "Accessories", href: "/p/accessories", dropdown: ["Cufflinks","Pens","Wallets","Belts"] },
  { label: "Brand New", href: "/shop?featured=true" },
  { label: "Blog", href: "/p/blog" },
];

const DEFAULT_FOOTER = [
  { title: "Shop", links: [
    { label: "All Watches", href: "/shop" },
    { label: "Featured", href: "/shop?featured=true" },
    { label: "Limited Edition", href: "/shop?category=limited-edition" },
    { label: "Track Order", href: "/track" },
  ]},
  { title: "Company", links: [
    { label: "About", href: "/about" },
    { label: "Contact", href: "/contact" },
    { label: "Staff Login", href: "/staff/login" },
  ]},
];

export default function DevMenus() {
  const [activeMenu, setActiveMenu] = useState("nav_menu");
  const [nav, setNav] = useState([]);
  const [footer, setFooter] = useState([]);

  useEffect(() => {
    api.get("/site/content/nav_menu").then((r) => setNav((r.data?.items?.length ? r.data.items : DEFAULT_NAV).map(normalizeNav))).catch(() => setNav(DEFAULT_NAV.map(normalizeNav)));
    api.get("/site/content/footer_menu").then((r) => setFooter((r.data?.items?.length ? r.data.items : DEFAULT_FOOTER))).catch(() => setFooter(DEFAULT_FOOTER));
  }, []);

  const saveNav = async () => {
    try { await api.put("/developer/content/nav_menu", { items: nav }); toast.success("Header menu saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const saveFooter = async () => {
    try { await api.put("/developer/content/footer_menu", { items: footer }); toast.success("Footer menu saved"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div><div className="overline mb-2">Navigation</div><h1 className="font-display text-4xl">Header & Footer Menus</h1></div>

      <div className="flex gap-2 flex-wrap">
        {[{ k: "nav_menu", label: "Header Menu", icon: ListOrdered }, { k: "footer_menu", label: "Footer Menu", icon: FolderTree }].map((t) => {
          const selected = activeMenu === t.k;
          return (
            <button
              key={t.k}
              data-testid={`menu-tab-${t.k}`}
              onClick={() => setActiveMenu(t.k)}
              className={`text-[11px] uppercase tracking-luxe px-4 py-2 border flex items-center gap-1.5 transition-colors ${selected ? "border-copper" : "border-white/15 hover:border-copper/60"}`}
              style={selected ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" } : undefined}
            >
              <t.icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>

      {activeMenu === "nav_menu" ? (
        <NavEditor items={nav} setItems={setNav} onSave={saveNav}/>
      ) : (
        <FooterEditor cols={footer} setCols={setFooter} onSave={saveFooter}/>
      )}
    </div>
  );
}

function normalizeNav(it) {
  // Accept legacy {dropdown:[strings]} OR new {sublinks:[{label,href}]} shapes
  if (Array.isArray(it.dropdown) && it.dropdown.length && typeof it.dropdown[0] === "string") {
    return { label: it.label, href: it.href, sublinks: it.dropdown.map((s) => ({ label: s, href: `${it.href || "/shop"}?q=${encodeURIComponent(s)}` })) };
  }
  return { label: it.label, href: it.href || "", sublinks: Array.isArray(it.sublinks) ? it.sublinks : [] };
}

function move(arr, idx, dir) {
  const ni = idx + dir; if (ni < 0 || ni >= arr.length) return arr;
  const cp = [...arr]; [cp[idx], cp[ni]] = [cp[ni], cp[idx]]; return cp;
}

function NavEditor({ items, setItems, onSave }) {
  const update = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  return (
    <div className="space-y-3">
      <div className="text-xs text-white/50">Header navigation. Each top-level item can have an optional dropdown of sub-links.</div>

      {items.map((it, i) => (
        <div key={i} className="card-luxe p-5" data-testid={`nav-item-${i}`}>
          <div className="flex justify-between items-start mb-3 gap-2">
            <div className="overline">Menu #{i + 1}</div>
            <div className="flex gap-2">
              <button onClick={() => setItems(move(items, i, -1))} className="p-1.5 border border-white/15 hover:border-copper" title="Up"><ArrowUp size={12}/></button>
              <button onClick={() => setItems(move(items, i, 1))} className="p-1.5 border border-white/15 hover:border-copper" title="Down"><ArrowDown size={12}/></button>
              <button onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 size={12}/></button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Label</div>
              <input data-testid={`nav-label-${i}`} className="input-box" value={it.label || ""} onChange={(e) => update(i, { label: e.target.value })}/>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Link (URL or page slug)</div>
              <input data-testid={`nav-href-${i}`} className="input-box" placeholder="/shop or /p/about or https://…" value={it.href || ""} onChange={(e) => update(i, { href: e.target.value })}/>
            </div>
          </div>

          {/* Sublinks */}
          <div className="mt-4 pl-4 border-l-2 border-copper/40">
            <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-2">Dropdown sub-links</div>
            {(it.sublinks || []).map((sl, si) => (
              <div key={si} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center">
                <input className="input-box" placeholder="Sublink label" value={sl.label || ""} onChange={(e) => update(i, { sublinks: it.sublinks.map((x, j) => j === si ? { ...x, label: e.target.value } : x) })}/>
                <input className="input-box" placeholder="Sublink URL" value={sl.href || ""} onChange={(e) => update(i, { sublinks: it.sublinks.map((x, j) => j === si ? { ...x, href: e.target.value } : x) })}/>
                <button onClick={() => update(i, { sublinks: it.sublinks.filter((_, j) => j !== si) })} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10" title="Remove"><Trash2 size={12}/></button>
              </div>
            ))}
            <button onClick={() => update(i, { sublinks: [...(it.sublinks || []), { label: "", href: "" }] })} className="text-[11px] uppercase tracking-luxe text-copper hover:text-[#A0834F] flex items-center gap-1"><Plus size={11}/> Add Sub-link</button>
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <button data-testid="nav-add" onClick={() => setItems([...items, { label: "New Menu", href: "/", sublinks: [] }])} className="btn-outline"><Plus size={13}/> Add Menu Item</button>
        <button data-testid="nav-save" onClick={onSave} className="btn-primary"><Save size={13}/> Save Header Menu</button>
      </div>
    </div>
  );
}

function FooterEditor({ cols, setCols, onSave }) {
  const update = (i, patch) => setCols(cols.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  return (
    <div className="space-y-3">
      <div className="text-xs text-white/50">Footer link columns. Up to 2 columns are rendered (Brand / Contact columns are auto-generated from Branding).</div>

      {cols.map((c, i) => (
        <div key={i} className="card-luxe p-5" data-testid={`footer-col-${i}`}>
          <div className="flex justify-between items-start mb-3 gap-2">
            <div className="overline">Column #{i + 1}</div>
            <div className="flex gap-2">
              <button onClick={() => setCols(move(cols, i, -1))} className="p-1.5 border border-white/15 hover:border-copper"><ArrowUp size={12}/></button>
              <button onClick={() => setCols(move(cols, i, 1))} className="p-1.5 border border-white/15 hover:border-copper"><ArrowDown size={12}/></button>
              <button onClick={() => setCols(cols.filter((_, idx) => idx !== i))} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 size={12}/></button>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Column Title</div>
          <input data-testid={`footer-title-${i}`} className="input-box mb-3" value={c.title || ""} onChange={(e) => update(i, { title: e.target.value })}/>

          {(c.links || []).map((l, li) => (
            <div key={li} className="grid md:grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center">
              <input className="input-box" placeholder="Label" value={l.label || ""} onChange={(e) => update(i, { links: c.links.map((x, j) => j === li ? { ...x, label: e.target.value } : x) })}/>
              <input className="input-box" placeholder="URL" value={l.href || ""} onChange={(e) => update(i, { links: c.links.map((x, j) => j === li ? { ...x, href: e.target.value } : x) })}/>
              <button onClick={() => update(i, { links: c.links.filter((_, j) => j !== li) })} className="p-1.5 border border-red-500/40 text-red-400 hover:bg-red-500/10"><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={() => update(i, { links: [...(c.links || []), { label: "", href: "" }] })} className="text-[11px] uppercase tracking-luxe text-copper hover:text-[#A0834F] flex items-center gap-1"><Plus size={11}/> Add Link</button>
        </div>
      ))}

      <div className="flex gap-3">
        <button data-testid="footer-add-col" onClick={() => setCols([...cols, { title: "New Column", links: [] }])} className="btn-outline"><Plus size={13}/> Add Column</button>
        <button data-testid="footer-save" onClick={onSave} className="btn-primary"><Save size={13}/> Save Footer Menu</button>
      </div>
    </div>
  );
}
